/**
 * Designboard – Step 1 (MVP)
 * --------------------------------------------------------------------------
 * Data persistence is deliberately wrapped behind `BoardStore`.
 * In Step 1 the store writes to localStorage.
 * In Step 2 ONLY the four methods (load/save/add/remove) get swapped to
 * fetch() against the App Proxy – the rest of the code stays untouched.
 * --------------------------------------------------------------------------
 */

(function () {
  'use strict';

  /* ======================================================================
   * BoardStore – the only place that changes in Step 2.
   * ====================================================================== */
  var BoardStore = {
    KEY: 'designboard:items',

    // Returns an array of items: { variantId, handle, title, image, price, url }
    load: function () {
      try {
        var raw = window.localStorage.getItem(this.KEY);
        return raw ? JSON.parse(raw) : [];
      } catch (e) {
        return [];
      }
    },

    save: function (items) {
      try {
        window.localStorage.setItem(this.KEY, JSON.stringify(items));
      } catch (e) {
        /* localStorage full or blocked – silently ignore in the MVP */
      }
      this._notify();
    },

    add: function (item) {
      var items = this.load();
      // Avoid duplicates by variantId
      var exists = items.some(function (i) {
        return String(i.variantId) === String(item.variantId);
      });
      if (!exists) {
        items.push(item);
        this.save(items);
      }
      return !exists;
    },

    remove: function (variantId) {
      var items = this.load().filter(function (i) {
        return String(i.variantId) !== String(variantId);
      });
      this.save(items);
    },

    clear: function () {
      this.save([]);
    },

    // Event so button counters etc. can react
    _notify: function () {
      document.dispatchEvent(
        new CustomEvent('designboard:changed', { detail: { count: this.load().length } })
      );
    }
  };

  // Expose globally so the product snippet can reach it
  window.Designboard = window.Designboard || {};
  window.Designboard.store = BoardStore;

  /* ======================================================================
   * Product page: "Add to designboard" button.
   * Bound via event delegation on [data-designboard-add] so it also works
   * inside the quick view / after AJAX re-renders.
   * ====================================================================== */
  document.addEventListener('click', function (evt) {
    var btn = evt.target.closest('[data-designboard-add]');
    if (!btn) return;

    evt.preventDefault();

    var item = {
      variantId: btn.getAttribute('data-variant-id'),
      handle: btn.getAttribute('data-handle'),
      title: btn.getAttribute('data-title'),
      image: btn.getAttribute('data-image'),
      price: btn.getAttribute('data-price'),
      url: btn.getAttribute('data-url')
    };

    if (!item.variantId) return;

    var added = BoardStore.add(item);

    // Visual feedback on the button
    var original = btn.getAttribute('data-label-default') || btn.textContent;
    btn.textContent = added
      ? (btn.getAttribute('data-label-added') || 'Auf der Designwand ✓')
      : (btn.getAttribute('data-label-exists') || 'Bereits auf der Wand');
    btn.classList.add('is-added');
    setTimeout(function () {
      btn.textContent = original;
      btn.classList.remove('is-added');
    }, 1800);
  });

  /* ======================================================================
   * Header counter (optional): every element with [data-designboard-count]
   * receives the current item count.
   * ====================================================================== */
  function refreshCounters() {
    var count = BoardStore.load().length;
    document.querySelectorAll('[data-designboard-count]').forEach(function (el) {
      el.textContent = count;
      el.hidden = count === 0;
    });
  }
  document.addEventListener('designboard:changed', refreshCounters);
  document.addEventListener('DOMContentLoaded', refreshCounters);

  /* ======================================================================
   * Variant change (Impulse): keep the button data in sync.
   * Impulse fires 'variantChange' and/or updates the hidden variant input.
   * We read the currently selected variant from the product form and write
   * it into the button.
   * ====================================================================== */
  function syncButtonVariant() {
    document.querySelectorAll('[data-designboard-add]').forEach(function (btn) {
      var form = btn.closest('form[action*="/cart/add"]')
        || btn.closest('.product-single, [data-section-type="product"]');
      if (!form) return;
      var input = form.querySelector('[name="id"]');
      if (input && input.value) {
        btn.setAttribute('data-variant-id', input.value);
      }
    });
  }
  // Impulse-specific event + generic fallback
  document.addEventListener('variantChange', syncButtonVariant);
  document.addEventListener('change', function (e) {
    if (e.target && e.target.name === 'id') syncButtonVariant();
  });

  /* ======================================================================
   * The designboard itself – only runs when the canvas container exists.
   * ====================================================================== */
  document.addEventListener('DOMContentLoaded', function () {
    var board = document.querySelector('[data-designboard-canvas]');
    if (!board) return;

    var emptyState = document.querySelector('[data-designboard-empty]');
    var addAllBtn = document.querySelector('[data-designboard-addall]');
    var clearBtn = document.querySelector('[data-designboard-clear]');
    var statusEl = document.querySelector('[data-designboard-status]');
    var topZ = 20;  // running counter to bring the touched tile to the front

    function setStatus(msg) {
      if (statusEl) statusEl.textContent = msg || '';
    }

    /* ---- Render ------------------------------------------------------- */
    function render() {
      var items = BoardStore.load();
      board.innerHTML = '';

      if (emptyState) emptyState.hidden = items.length > 0;
      if (addAllBtn) addAllBtn.disabled = items.length === 0;

      items.forEach(function (item, index) {
        var tile = document.createElement('div');
        tile.className = 'designboard__tile';
        tile.setAttribute('data-variant-id', item.variantId);

        // Restore saved position – otherwise stagger new tiles into a stack
        if (item.x != null && item.y != null) {
          tile.style.left = item.x + 'px';
          tile.style.top = item.y + 'px';
        } else {
          var offset = 24 + (index % 8) * 28;
          tile.style.left = offset + 'px';
          tile.style.top = offset + 'px';
        }

        var img = document.createElement('img');
        img.className = 'designboard__img';
        img.src = item.image || '';
        img.alt = item.title || '';
        img.draggable = false;

        var resizeHandle = document.createElement('div');
        resizeHandle.className = 'designboard__resize';
        resizeHandle.setAttribute('aria-hidden', 'true');

        tile.appendChild(img);
        tile.appendChild(resizeHandle);
        board.appendChild(tile);

        // Restore saved size if present
        if (item.w != null && item.h != null) {
          tile.style.width = item.w + 'px';
          tile.style.height = item.h + 'px';
        }

        makeDraggable(tile, item.variantId);
        makeResizable(tile, item.variantId, resizeHandle);
      });

      renderList();
    }

    /* ---- The list beside the board (thumbnail + title + remove) -------- */
    function renderList() {
      var listEl = document.querySelector('[data-designboard-list]');
      if (!listEl) return;

      var items = BoardStore.load();
      listEl.innerHTML = '';

      items.forEach(function (item) {
        var row = document.createElement('div');
        row.className = 'designboard-list__row';

        var thumb = document.createElement('img');
        thumb.className = 'designboard-list__thumb';
        thumb.src = item.image || '';
        thumb.alt = '';

        var title = document.createElement('span');
        title.className = 'designboard-list__title';
        title.textContent = item.title || '';

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'designboard-list__remove';
        remove.textContent = 'Entfernen';
        remove.addEventListener('click', function () {
          BoardStore.remove(item.variantId);
          render();
        });

        row.appendChild(thumb);
        row.appendChild(title);
        row.appendChild(remove);
        listEl.appendChild(row);
      });
    }

    /* ---- Drag & drop via pointer events (touch-safe) ------------------ */
    function makeDraggable(tile, variantId) {
      var startX, startY, originLeft, originTop, dragging = false;

      tile.addEventListener('pointerdown', function (e) {
        // Don't start a drag when grabbing the resize handle
        if (e.target.closest('.designboard__resize')) return;

        dragging = true;
        tile.setPointerCapture(e.pointerId);
        tile.classList.add('is-dragging');
        topZ += 1;
        tile.style.zIndex = topZ;

        var rect = tile.getBoundingClientRect();
        var boardRect = board.getBoundingClientRect();
        originLeft = rect.left - boardRect.left;
        originTop = rect.top - boardRect.top;
        startX = e.clientX;
        startY = e.clientY;

        // Position absolutely so it can be moved freely
        tile.style.position = 'absolute';
        tile.style.left = originLeft + 'px';
        tile.style.top = originTop + 'px';
      });

      tile.addEventListener('pointermove', function (e) {
        if (!dragging) return;
        var dx = e.clientX - startX;
        var dy = e.clientY - startY;

        var boardRect = board.getBoundingClientRect();
        var maxX = boardRect.width - tile.offsetWidth;
        var maxY = boardRect.height - tile.offsetHeight;

        var newX = Math.min(Math.max(0, originLeft + dx), Math.max(0, maxX));
        var newY = Math.min(Math.max(0, originTop + dy), Math.max(0, maxY));

        tile.style.left = newX + 'px';
        tile.style.top = newY + 'px';
      });

      function endDrag(e) {
        if (!dragging) return;
        dragging = false;
        tile.classList.remove('is-dragging');

        // Persist position in the store
        var items = BoardStore.load();
        var hit = items.find(function (i) {
          return String(i.variantId) === String(variantId);
        });
        if (hit) {
          hit.x = parseInt(tile.style.left, 10) || 0;
          hit.y = parseInt(tile.style.top, 10) || 0;
          BoardStore.save(items);
        }
      }

      tile.addEventListener('pointerup', endDrag);
      tile.addEventListener('pointercancel', endDrag);
    }

    /* ---- Resize via corner handle (pointer events, touch-friendly) ----
     * Free resize (no fixed aspect ratio). The image uses object-fit:cover,
     * so the fabric pattern is cropped, not distorted. */
    function makeResizable(tile, variantId, handle) {
      var startX, startY, startW, startH, resizing = false;
      var MIN = 80;   // minimum tile size in px
      var MAX = 560;  // maximum tile size in px

      handle.addEventListener('pointerdown', function (e) {
        e.stopPropagation();  // don't let the tile-drag handler fire
        resizing = true;
        handle.setPointerCapture(e.pointerId);
        tile.classList.add('is-resizing');
        startX = e.clientX;
        startY = e.clientY;
        startW = tile.offsetWidth;
        startH = tile.offsetHeight;
      });

      handle.addEventListener('pointermove', function (e) {
        if (!resizing) return;
        var newW = Math.min(Math.max(MIN, startW + (e.clientX - startX)), MAX);
        var newH = Math.min(Math.max(MIN, startH + (e.clientY - startY)), MAX);
        tile.style.width = newW + 'px';
        tile.style.height = newH + 'px';
      });

      function endResize() {
        if (!resizing) return;
        resizing = false;
        tile.classList.remove('is-resizing');
        var items = BoardStore.load();
        var hit = items.find(function (i) {
          return String(i.variantId) === String(variantId);
        });
        if (hit) {
          hit.w = tile.offsetWidth;
          hit.h = tile.offsetHeight;
          BoardStore.save(items);
        }
      }

      handle.addEventListener('pointerup', endResize);
      handle.addEventListener('pointercancel', endResize);
    }
    if (addAllBtn) {
      addAllBtn.addEventListener('click', function () {
        var items = BoardStore.load();
        if (!items.length) return;

        addAllBtn.disabled = true;
        setStatus('Lege Artikel in den Warenkorb …');

        var payload = {
          items: items.map(function (i) {
            return { id: Number(i.variantId), quantity: 1 };
          })
        };

        fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
          .then(function (res) {
            if (!res.ok) throw new Error('Cart add failed');
            return res.json();
          })
          .then(function () {
            setStatus('Alles im Warenkorb. Weiterleitung …');
            // Let Impulse refresh the cart drawer, otherwise fall back to /cart
            window.location.href = '/cart';
          })
          .catch(function () {
            setStatus('Etwas ist schiefgelaufen. Bitte erneut versuchen.');
            addAllBtn.disabled = false;
          });
      });
    }

    /* ---- Clear the board ---------------------------------------------- */
    if (clearBtn) {
      clearBtn.addEventListener('click', function () {
        if (window.confirm('Wirklich die ganze Designwand leeren?')) {
          BoardStore.clear();
          render();
        }
      });
    }

    render();
  });
})();